/**
 * OnePagerRow
 * Memoized row component for KPI data cells in the One Pager table.
 * Renders one cell per PagerColumn with target-met coloring.
 */

import React from 'react';
import { Platform, Text, View } from 'react-native';
import { GroupedRow, PagerColumn } from '../../constants/onePagerConstants';
import { formatKpiValue, formatTargetValue } from '../../utils/onePagerFormatters';
import { isMetricBad } from '../../utils/onePagerStatus';
import { createOnePagerTableStyles } from '../../styles/onePagerTableStyles';
import KpiTargetTooltip from '../KpiTargetTooltip';
import { useTheme } from '../../../../theme/useTheme';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface OnePagerRowProps {
	readonly row: GroupedRow;
	readonly index: number;
	readonly columns: readonly PagerColumn[];
	readonly sparcBoundaryIndices: ReadonlySet<number>;
	readonly cellWidth?: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

const OnePagerRow: React.FC<OnePagerRowProps> = ({ row, index, columns, sparcBoundaryIndices, cellWidth }) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
	const styles = createOnePagerTableStyles(isDark, colors);
	const isEven = index % 2 === 0;

	return (
		<View style={[styles.dataRow, isEven ? styles.dataRowEven : styles.dataRowOdd]}>
			{columns.map((col, colIdx) => {
				const value = row.vals[col.id];
				const targetVal = row.targets?.[col.id];
				// Colour comes solely from the backend's tri-state flag — the API owns
				// all threshold logic. See docs/one-pager-color-coding-contract.md.
				const isBad = isMetricBad(row, col.id);
				const isBoundary = sparcBoundaryIndices.has(colIdx);

				return (
					<View key={col.id} style={[styles.kpiCell, cellWidth !== undefined && { width: cellWidth }, isBoundary && styles.sparcBorderRight]}>
						<KpiTargetTooltip
							metricName={col.label}
							actualValue={formatKpiValue(value, col)}
							targetValue={targetVal !== undefined ? formatTargetValue(targetVal, col) : null}
							varianceActualValue={col.id === 'gap' ? value ?? null : undefined}
						>
							<Text
								style={[
									styles.kpiCellText,
									isBad && styles.kpiCellDanger,
								]}
								numberOfLines={1}
							>
								{formatKpiValue(value, col)}
							</Text>
						</KpiTargetTooltip>
					</View>
				);
			})}
		</View>
	);
};

OnePagerRow.displayName = 'OnePagerRow';

export default React.memo(OnePagerRow);
